import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Alert,
    CircularProgress,
    Typography,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Pagination,
    IconButton,
    Toolbar,
    LinearProgress,
    Skeleton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderIcon from '@mui/icons-material/Folder';
import TableChartIcon from '@mui/icons-material/TableChart';
import StorageIcon from '@mui/icons-material/Storage';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const PAGE_SIZE = 20;
const SIDEBAR_WIDTH = 280;

/**
 * Explorer dữ liệu kiểu pgAdmin / MongoDB Compass:
 * - Cột trái: cây thư mục (collections / tables / collections Firestore / prefix MinIO)
 * - Cột phải: bảng dữ liệu + toolbar (Refresh, phân trang)
 */
const FetchDataDialog = ({ open, onClose, projectName, source, fetchData, result, isLoading, error }) => {
    const [selectedNode, setSelectedNode] = useState(null); // { type: 'collection'|'table'|'path', id: string, label: string }
    const [treeNodes, setTreeNodes] = useState([]); // cache collections/tables để tree không mất khi đang xem data
    const [prefix, setPrefix] = useState('');
    const [page, setPage] = useState(0);
    const [limit] = useState(PAGE_SIZE);

    const sourceType = source?.source_type || '';
    const isMongo = sourceType === 'mongodb';
    const isPostgres = sourceType === 'postgres';
    const isMinio = sourceType === 'minio';
    const isFirebase = sourceType === 'firebase';

    const items = result?.items || [];
    // Dùng cache tree khi đang xem data; khi mới load list thì cập nhật cache
    const collections = (result?.items?.length ? treeNodes : result?.collections) || result?.collections || [];
    const tables = (result?.items?.length ? treeNodes : result?.tables) || result?.tables || [];
    const firebaseCollections = (result?.items?.length ? treeNodes : result?.collections) || result?.collections || [];
    const total = result?.total ?? 0;
    const fetchError = result?.error || error?.data?.detail || error?.message;

    // Khi mở dialog: tải danh sách collections/tables (và Firestore collections)
    useEffect(() => {
        if (!open || !source) return;
        setSelectedNode(null);
        setTreeNodes([]);
        setPrefix('');
        setPage(0);
        if (isMongo || isPostgres || isFirebase) {
            fetchData({ projectName, sourceId: source.id, params: {} });
        }
    }, [open, source?.id, sourceType, isMongo, isPostgres, isFirebase, projectName]);

    // Lưu danh sách tree khi API trả về (để không mất khi đang xem data)
    useEffect(() => {
        if (!result) return;
        if (result.collections && result.collections.length && !result.items?.length) {
            setTreeNodes(result.collections);
        }
        if (result.tables && result.tables.length && !result.items?.length) {
            setTreeNodes(result.tables);
        }
    }, [result?.collections, result?.tables, result?.items?.length]);

    // Khi chọn node và đang xem data: tải theo trang
    useEffect(() => {
        if (!open || !source || !selectedNode) return;
        if (isMongo && selectedNode.type === 'collection') {
            fetchData({ projectName, sourceId: source.id, params: { collection: selectedNode.id, limit, skip: page * limit } });
        } else if (isPostgres && selectedNode.type === 'table') {
            fetchData({ projectName, sourceId: source.id, params: { table: selectedNode.id, limit, offset: page * limit } });
        } else if (isFirebase && selectedNode.type === 'collection') {
            fetchData({ projectName, sourceId: source.id, params: { path: selectedNode.id, limit } });
        } else if (isMinio && selectedNode.type === 'prefix') {
            fetchData({ projectName, sourceId: source.id, params: { prefix: selectedNode.id || '', limit } });
        }
    }, [selectedNode, page, open, source?.id, isMongo, isPostgres, isFirebase, isMinio, projectName, limit]);

    const handleRefreshList = () => {
        if (isMongo || isPostgres || isFirebase) {
            fetchData({ projectName, sourceId: source.id, params: {} });
        }
    };

    const handleRefreshData = () => {
        if (!selectedNode) return;
        setPage(0);
        if (isMongo && selectedNode.type === 'collection') {
            fetchData({ projectName, sourceId: source.id, params: { collection: selectedNode.id, limit, skip: 0 } });
        } else if (isPostgres && selectedNode.type === 'table') {
            fetchData({ projectName, sourceId: source.id, params: { table: selectedNode.id, limit, offset: 0 } });
        } else if (isFirebase && selectedNode.type === 'collection') {
            fetchData({ projectName, sourceId: source.id, params: { path: selectedNode.id, limit } });
        } else if (isMinio && selectedNode.type === 'prefix') {
            fetchData({ projectName, sourceId: source.id, params: { prefix: selectedNode.id || '', limit } });
        }
    };

    const handleLoadMinio = () => {
        setSelectedNode({ type: 'prefix', id: prefix, label: prefix || '(gốc bucket)' });
        setPage(0);
    };

    const columns = React.useMemo(() => {
        if (!items.length) return [];
        const keys = Object.keys(items[0]);
        return keys.map((key) => ({
            field: key,
            headerName: key,
            flex: 1,
            minWidth: 120,
            valueGetter: (val) => (typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val ?? ''))
        }));
    }, [items]);

    const rows = items.map((row, i) => ({ id: page * limit + i, ...row }));

    const treeLabel = isMongo ? 'Collections' : isPostgres ? 'Tables' : isFirebase ? 'Collections (Firestore)' : 'Bucket';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
            <DialogTitle sx={{ pb: 0 }}>
                Xem dữ liệu – {source?.name}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {sourceType}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {isLoading && (
                    <LinearProgress sx={{ flexShrink: 0 }} />
                )}
                {fetchError && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {fetchError}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    {/* Sidebar trái: cây thư mục */}
                    <Box
                        sx={{
                            width: SIDEBAR_WIDTH,
                            borderRight: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'grey.50'
                        }}
                    >
                        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {treeLabel}
                            </Typography>
                            {(isMongo || isPostgres || isFirebase) && (
                                <IconButton size="small" onClick={handleRefreshList} disabled={isLoading} title="Làm mới danh sách">
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                        <Divider />

                        {isMongo && (
                            <List dense sx={{ overflow: 'auto', flex: 1 }}>
                                {isLoading && !(Array.isArray(collections) && collections.length) && (
                                    <Box sx={{ px: 1, py: 0.5 }}>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 0.5 }} />
                                        ))}
                                    </Box>
                                )}
                                {(Array.isArray(collections) ? collections : []).map((c) => (
                                    <ListItemButton
                                        key={c}
                                        selected={selectedNode?.type === 'collection' && selectedNode?.id === c}
                                        onClick={() => {
                                            setSelectedNode({ type: 'collection', id: c, label: c });
                                            setPage(0);
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <FolderIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={c} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                    </ListItemButton>
                                ))}
                                {!isLoading && (!Array.isArray(collections) || collections.length === 0) && !fetchError && (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                                        Không có collection.
                                    </Typography>
                                )}
                            </List>
                        )}

                        {isPostgres && (
                            <List dense sx={{ overflow: 'auto', flex: 1 }}>
                                {isLoading && !(Array.isArray(tables) && tables.length) && (
                                    <Box sx={{ px: 1, py: 0.5 }}>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 0.5 }} />
                                        ))}
                                    </Box>
                                )}
                                {(Array.isArray(tables) ? tables : []).map((t) => {
                                    const fullName = typeof t === 'object' ? `${t.table_schema}.${t.table_name}` : t;
                                    const tableId = typeof t === 'object' ? fullName : t;
                                    return (
                                        <ListItemButton
                                            key={fullName}
                                            selected={selectedNode?.type === 'table' && selectedNode?.id === tableId}
                                            onClick={() => {
                                                setSelectedNode({ type: 'table', id: tableId, label: fullName });
                                                setPage(0);
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <TableChartIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={fullName} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                        </ListItemButton>
                                    );
                                })}
                                {!isLoading && (!Array.isArray(tables) || tables.length === 0) && !fetchError && (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                                        Không có bảng.
                                    </Typography>
                                )}
                            </List>
                        )}

                        {isFirebase && (
                            <List dense sx={{ overflow: 'auto', flex: 1 }}>
                                {isLoading && !(Array.isArray(firebaseCollections) && firebaseCollections.length) && (
                                    <Box sx={{ px: 1, py: 0.5 }}>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 0.5 }} />
                                        ))}
                                    </Box>
                                )}
                                {(Array.isArray(firebaseCollections) ? firebaseCollections : []).map((c) => (
                                    <ListItemButton
                                        key={c}
                                        selected={selectedNode?.type === 'collection' && selectedNode?.id === c}
                                        onClick={() => {
                                            setSelectedNode({ type: 'collection', id: c, label: c });
                                            setPage(0);
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <FolderIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={c} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                    </ListItemButton>
                                ))}
                                {!isLoading && firebaseCollections.length === 0 && !fetchError && (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                                        Không có collection.
                                    </Typography>
                                )}
                            </List>
                        )}

                        {isMinio && (
                            <Box sx={{ p: 2 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Prefix"
                                    value={prefix}
                                    onChange={(e) => setPrefix(e.target.value)}
                                    placeholder="data/ hoặc để trống"
                                    helperText="Đường dẫn trong bucket"
                                />
                                <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleLoadMinio} disabled={isLoading} startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}>
                                    {isLoading ? 'Đang tải...' : 'Liệt kê đối tượng'}
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* Vùng phải: toolbar + data grid */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {selectedNode || (isMinio && items.length > 0) ? (
                            <>
                                <Toolbar variant="dense" sx={{ borderBottom: 1, borderColor: 'divider', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedNode ? selectedNode.label : prefix || '(gốc)'}
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <IconButton size="small" onClick={handleRefreshData} disabled={isLoading} title="Làm mới dữ liệu">
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                    {(isMongo || isPostgres) && (
                                        <Pagination
                                            count={Math.ceil(total / limit) || 1}
                                            page={page + 1}
                                            onChange={(e, p) => setPage(p - 1)}
                                            color="primary"
                                            size="small"
                                            showFirstButton
                                            showLastButton
                                        />
                                    )}
                                </Toolbar>
                                <Box sx={{ flex: 1, minHeight: 400, p: 2, position: 'relative' }}>
                                    {isLoading && items.length === 0 && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 2 }}>
                                            <CircularProgress size={48} />
                                            <Typography variant="body2" color="text.secondary">Đang tải dữ liệu...</Typography>
                                        </Box>
                                    )}
                                    {isLoading && items.length > 0 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'rgba(255,255,255,0.7)',
                                                zIndex: 10,
                                                borderRadius: 1
                                            }}
                                        >
                                            <CircularProgress size={48} />
                                            <Typography variant="body2" color="text.secondary" sx={{ position: 'absolute', mt: 6 }}>Đang làm mới...</Typography>
                                        </Box>
                                    )}
                                    {!isLoading && items.length > 0 && (
                                        <>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                Tổng: {total} {isMongo ? 'document' : isPostgres ? 'dòng' : 'bản ghi'}
                                            </Typography>
                                            <DataGrid
                                                rows={rows}
                                                columns={columns}
                                                pageSizeOptions={[PAGE_SIZE]}
                                                disableRowSelectionOnClick
                                                density="compact"
                                                sx={{ border: 'none' }}
                                                autoHeight
                                            />
                                        </>
                                    )}
                                    {!isLoading && items.length === 0 && selectedNode && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
                                            <InsertDriveFileIcon sx={{ fontSize: 48, mb: 1 }} />
                                            <Typography variant="body2">Không có dữ liệu</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.secondary',
                                    p: 4
                                }}
                            >
                                <StorageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                <Typography variant="body1">
                                    {isMinio ? 'Nhập prefix và bấm "Liệt kê đối tượng"' : 'Chọn một mục ở cột trái để xem dữ liệu'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Đóng</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FetchDataDialog;
